import { useEffect, useMemo, useState } from "react";
import { Save } from "lucide-react";

import {
  useSyncVisitStates,
  useUpdateSyncVisitStates,
} from "@/lib/api/clinicianHooks";
import { US_STATES } from "@/lib/usStates";

/**
 * Supervisor-edited list of states whose new orders need a live (sync) visit
 * before a prescription. Changes apply to orders created afterwards; orders
 * already waiting keep the requirement they were given.
 */
export function SyncVisitStates({ enabled }: { enabled: boolean }) {
  const states = useSyncVisitStates(enabled);
  const update = useUpdateSyncVisitStates();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [note, setNote] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!dirty && states.data) setSelected(new Set(states.data.required));
  }, [dirty, states.data]);

  const saved = useMemo(
    () => new Set(states.data?.required ?? []),
    [states.data],
  );
  const adding = [...selected].filter((code) => !saved.has(code)).sort();
  const removing = [...saved].filter((code) => !selected.has(code)).sort();
  const known = new Set(states.data?.states.map((row) => row.state_code) ?? []);

  function toggle(code: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
    setDirty(true);
    setNotice(null);
  }

  async function save() {
    try {
      await update.mutateAsync({
        required: [...selected].sort(),
        note: note.trim() || undefined,
      });
      setDirty(false);
      setNote("");
      setNotice("Sync-visit states saved. They apply to new orders.");
    } catch {
      setNotice("Sync-visit states could not be saved. Nothing was changed.");
    }
  }

  return (
    <section className="rounded-lg border border-line bg-raised overflow-hidden">
      <div className="border-b border-line p-16">
        <h2 className="type-heading-md">Sync-visit states</h2>
        <p className="mt-4 type-body-sm text-ink-muted max-w-[760px]">
          New orders shipping to a highlighted state need a live visit with the
          prescriber before a prescription can be written. Changes apply to
          orders placed afterwards; orders already waiting keep their current
          requirement.
        </p>
      </div>
      {states.isLoading ? (
        <p className="p-20 type-body-sm text-ink-muted">Loading states…</p>
      ) : states.isError ? (
        <p className="p-20 type-body-sm text-danger">
          The state list could not be loaded.
        </p>
      ) : (
        <div className="p-16 space-y-14">
          <div
            className="grid grid-cols-[repeat(auto-fill,minmax(4.25rem,1fr))] gap-6"
            role="group"
            aria-label="States that require a sync visit"
          >
            {US_STATES.filter((state) => known.has(state.value)).map(
              (state) => {
                const on = selected.has(state.value);
                return (
                  <button
                    key={state.value}
                    type="button"
                    aria-pressed={on}
                    title={state.label}
                    onClick={() => toggle(state.value)}
                    className={`rounded border px-8 py-6 type-label-sm tabular-nums focus-visible:outline-none focus-visible:shadow-focus ${
                      on
                        ? "border-ink bg-ink text-page"
                        : "border-line bg-page text-ink-muted hover:border-ink"
                    }`}
                  >
                    {state.value}
                  </button>
                );
              },
            )}
          </div>
          <p className="type-body-sm">
            <span className="text-ink-muted">Sync required: </span>
            {[...selected].sort().join(", ") || "none"}
          </p>
          {dirty && (adding.length > 0 || removing.length > 0) && (
            <div className="rounded border border-line-subtle bg-page p-12 space-y-8">
              <p className="type-body-sm">
                {adding.length > 0 && <>Adding {adding.join(", ")}. </>}
                {removing.length > 0 && <>Removing {removing.join(", ")}.</>}
              </p>
              <label className="block">
                <span className="type-label-xs text-ink-muted">
                  Reason (kept with the change)
                </span>
                <input
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  maxLength={500}
                  placeholder="e.g. Q4 compliance review"
                  className="mt-4 w-full rounded border border-line bg-page px-10 py-8 type-body-sm focus-visible:outline-none focus-visible:shadow-focus"
                />
              </label>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-12">
            <button
              type="button"
              onClick={save}
              disabled={!dirty || update.isPending}
              className="inline-flex items-center gap-6 rounded bg-ink px-12 py-8 type-label-sm text-page disabled:opacity-50"
            >
              <Save size={14} aria-hidden />
              {update.isPending ? "Saving…" : "Save states"}
            </button>
            {dirty && (
              <button
                type="button"
                onClick={() => {
                  setDirty(false);
                  setNote("");
                }}
                className="type-label-sm text-ink-muted underline"
              >
                Discard changes
              </button>
            )}
            {notice && (
              <p
                role="status"
                className={
                  notice.includes("could not") ? "text-danger" : "text-success"
                }
              >
                {notice}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
